import { Link } from '../../../components/Link';
import {
  Lead,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
  Tok,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('role-based-email-addresses');

export default function RoleBasedEmailPage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'A role-based address names a function rather than a person — info@, sales@, support@. Several people read it and nobody owns it, which is why it produces more complaints and less engagement than a named address. Some of them are also required to exist by RFC 2142, so they are not a defect.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="The problem is shared ownership, not the name">
        <p>
          Every difficulty with these addresses follows from one fact: no individual is responsible
          for the inbox. Nobody subscribed, so nobody feels they can unsubscribe, and the person who
          opens your message on Tuesday is not the one who opened it on Monday.
        </p>
        <LearnList>
          <li>
            <Lead>Higher complaint rate.</Lead> The reader did not sign up, so "report spam" feels
            more legitimate than unsubscribing on a colleague's behalf. Complaints are the
            heaviest negative input into{' '}
            <Link to="/learn/sender-reputation" className="text-accent underline underline-offset-4">
              sender reputation
            </Link>
            , and Gmail's stated ceiling is 0.3%.
          </li>
          <li>
            <Lead>Lower engagement.</Lead> Shared inboxes get triaged, not read. Low opens on a
            segment teach a filter that your mail is unwanted — which then affects your named
            recipients too.
          </li>
          <li>
            <Lead>Consent is genuinely ambiguous.</Lead> One person entering{' '}
            <Tok>info@</Tok> into a form has not obtained consent from everyone who reads it. Under
            GDPR that is a real question, not a technicality.
          </li>
          <li>
            <Lead>Some are monitored by people looking for problems.</Lead>{' '}
            <Tok>abuse@</Tok> and <Tok>postmaster@</Tok> exist to receive complaints. Marketing to
            them is the fastest route to being reported by someone whose job is reporting.
          </li>
        </LearnList>
      </LearnSection>

      <LearnSection title="Not all role addresses are equal">
        <p>
          Blanket blocking is the usual implementation and it is too blunt. These behave very
          differently:
        </p>
        <LearnTable
          head={['Address', 'What it is', 'Marketing to it']}
          rows={[
            [<Tok key="a">abuse@</Tok>, 'Required by RFC 2142. Receives complaints', <Lead key="b">Never</Lead>],
            [<Tok key="c">postmaster@</Tok>, 'Required by RFC 5321. Mail operations', <Lead key="d">Never</Lead>],
            [<Tok key="e">noreply@ / no-reply@</Tok>, 'Usually unmonitored, often unable to receive', <Lead key="f">Never — likely to bounce</Lead>],
            [<Tok key="g">security@</Tok>, 'Vulnerability reports', 'Never'],
            [<Tok key="h">info@ / contact@ / hello@</Tok>, 'The company’s front door', 'Often the only address a small business has'],
            [<Tok key="i">sales@</Tok>, 'Staffed, and wants to be sold to less than you think', 'Legitimate for B2B, with care'],
            [<Tok key="j">support@ / help@</Tok>, 'Staffed by people with a queue', 'Poor fit for marketing'],
            [<Tok key="k">billing@ / accounts@</Tok>, 'Finance', <Lead key="l">Fine for invoices, wrong for marketing</Lead>],
          ]}
        />
        <p>
          The last row is the one blanket rules break. Transactional mail — invoices, receipts,
          renewal notices — belongs at <Tok>billing@</Tok> or <Tok>accounts@</Tok>, and a signup
          form that refuses those addresses is refusing the correct answer.
        </p>
      </LearnSection>

      <LearnSection title="When blocking costs more than it saves">
        <p>
          The case against a hard block, which is stronger than most implementations assume:
        </p>
        <LearnSubsection title="For many businesses it is the only address">
          <p>
            A two-person consultancy publishes <Tok>info@</Tok> and nothing else. Refusing it at
            signup refuses the customer, and they will not hunt for an alternative — they will
            leave, and you will never learn why.
          </p>
        </LearnSubsection>
        <LearnSubsection title="B2B lead capture depends on them">
          <p>
            If you sell to companies, a meaningful share of genuine inbound arrives from a shared
            address. Blocking at the form is choosing list hygiene over pipeline, which is rarely
            the trade you meant to make.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Transactional mail must reach them">
          <p>
            Even where marketing to <Tok>accounts@</Tok> is wrong, invoicing it is right. A block
            applied at the account level rather than the mailing level breaks billing.
          </p>
        </LearnSubsection>
      </LearnSection>

      <LearnSection title="The policy that actually works">
        <p>
          Separate the decision to <em>accept</em> an address from the decision to <em>market</em>{' '}
          to it. Those are different questions and most systems conflate them.
        </p>
        <LearnTable
          head={['Context', 'Recommended policy']}
          rows={[
            ['Account signup', <Lead key="a">Accept everything except abuse@, postmaster@, security@, noreply@</Lead>],
            ['Transactional mail', 'Send to any address the customer nominated, including billing@'],
            ['Marketing list', 'Accept, but segment separately and watch complaints closely'],
            ['Cold outreach', <Lead key="b">Do not include role addresses at all</Lead>],
            ['Bought list containing them', 'The role address is the least of the problems'],
          ]}
        />
        <p>
          The four never-send addresses are worth hard-coding: three of them exist specifically to
          receive complaints, and <Tok>noreply@</Tok> frequently cannot receive at all, so it turns
          into a{' '}
          <Link to="/learn/email-bounce-rate" className="text-accent underline underline-offset-4">
            hard bounce
          </Link>
          . Everything else is a segmentation decision, not a validity one.
        </p>
      </LearnSection>

      <LearnSection title="Detecting them">
        <p>
          Straightforward — match the local part against a known list, case-insensitively, before
          any other checks. Two cautions worth building in:
        </p>
        <LearnList>
          <li>
            <Lead>Match exactly, not by prefix.</Lead> <Tok>info@</Tok> is a role address;{' '}
            <Tok>infoteam@</Tok> and <Tok>information.services@</Tok> may be real people or real
            departments. Substring matching produces false positives on names.
          </li>
          <li>
            <Lead>Personal domains change the reading.</Lead>{' '}
            <Tok>sales@gmail.com</Tok> is an individual who chose a username, not a shared inbox.
            The role signal only means anything on a domain the organisation controls — which,
            confusingly, is also often a{' '}
            <Link to="/learn/catch-all-email" className="text-accent underline underline-offset-4">
              catch-all
            </Link>
            .
          </li>
        </LearnList>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'What is a role-based email address?',
    a: 'One that names a function rather than a person — info@, sales@, support@, billing@. Several people read it and no individual owns it, which is what makes it behave differently from a named address for both consent and engagement.',
  },
  {
    q: 'Should I block role-based addresses at signup?',
    a: 'Only abuse@, postmaster@, security@ and noreply@. The first three exist to receive complaints and the fourth often cannot receive at all. Everything else, including info@, is frequently the only address a small business publishes — blocking it refuses the customer.',
  },
  {
    q: 'Why do role addresses hurt deliverability?',
    a: 'Because the person reading did not sign up, so marking it as spam feels more reasonable than unsubscribing on a colleague’s behalf. Complaints are the heaviest negative reputation input, and shared inboxes also get triaged rather than read, which drags engagement down.',
  },
  {
    q: 'Can I send transactional email to billing@ or accounts@?',
    a: 'Yes — that is exactly where it belongs. The distinction is between accepting an address and marketing to it. Invoices and renewal notices should go to whatever address the customer nominated; a policy that blocks these at account level breaks billing.',
  },
  {
    q: 'How do I detect a role address?',
    a: 'Match the local part against a known list, case-insensitively, and match it exactly rather than as a prefix. infoteam@ and information.services@ are not info@, and substring matching turns real names and departments into false positives.',
  },
  {
    q: 'Is sales@gmail.com a role address?',
    a: 'Not meaningfully. On a consumer domain that is an individual who happened to pick that username, not a shared company inbox. The role signal only carries weight on a domain the organisation actually controls.',
  },
];
